
import React, { useState, useEffect } from 'react';
import { auth, googleProvider, db } from '../../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { GraduationCap, Building2, Loader2, ArrowLeft, ShieldCheck, X, FileText } from 'lucide-react';
import { PolicySettingModel, CompanyName } from '../../models';

interface LoginProps {
    onBack: () => void;
    onSuccess: (user: any) => void;
    onBranchLogin: () => void;
}

const Login: React.FC<LoginProps> = ({ onBack, onSuccess, onBranchLogin }) => {
    const [loading, setLoading] = useState(false);
    const [policySettings, setPolicySettings] = useState<PolicySettingModel | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [activePolicy, setActivePolicy] = useState<{ title: string; content: string } | null>(null);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const docRef = doc(db, 'settings', 'policy');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setPolicySettings(docSnap.data() as PolicySettingModel);
                }
            } catch (error) {
                console.error("Error fetching policy:", error);
            }
        };
        fetchPolicy();
    }, []);

    const openPolicy = (type: 'tos' | 'privacy') => {
        if (!policySettings) return;
        let title = '';
        let content = '';

        if (type === 'tos') {
            title = '서비스 이용약관';
            content = policySettings.tos;
        } else {
            title = '개인정보 처리방침';
            content = policySettings.privacy;
        }

        setActivePolicy({ title, content });
        setModalOpen(true);
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        try {
            const result = await signInWithPopup(auth, googleProvider);
            onSuccess(result.user);
        } catch (error) {
            console.error("Login failed:", error);
            alert('로그인에 실패했습니다. 다시 시도해 주세요.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 md:p-8 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-100/50 blur-[100px] rounded-full"></div>
                <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-violet-100/50 blur-[100px] rounded-full"></div>
            </div>

            <button
                onClick={onBack}
                className="absolute top-4 left-4 md:top-8 md:left-8 flex items-center gap-2 text-slate-400 hover:text-slate-800 transition-colors font-bold text-xs uppercase tracking-widest group z-10"
            >
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center group-hover:bg-slate-50 transition-all">
                    <ArrowLeft size={16} />
                </div>
                대체화면으로 돌아가기
            </button>

            <div className="w-full max-w-md bg-white rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 shadow-2xl relative border border-slate-100 animate-in fade-in zoom-in duration-500">
                <div className="flex flex-col items-center text-center space-y-8 md:space-y-10">
                    <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white shadow-xl shadow-indigo-100 rotate-6">
                        <ShieldCheck size={40} />
                    </div>

                    <div className="space-y-4">
                        <h2 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight">서비스 로그인</h2>
                        <p className="text-slate-400 font-medium leading-relaxed px-4">
                            {CompanyName}의 '학생 이용자'는 <br /> 구글 계정으로 로그인해 주세요.
                        </p>
                    </div>

                    <button
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-4 bg-white border-2 border-slate-100 p-4 rounded-2xl font-black text-slate-700 hover:bg-slate-50 hover:border-slate-200 transition-all active:scale-95 shadow-sm group disabled:bg-slate-50"
                    >
                        {loading ? (
                            <Loader2 className="animate-spin text-indigo-600" size={24} />
                        ) : (
                            <>
                                <div className="bg-indigo-50 p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                                    <img
                                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                        alt="Google Logo"
                                        className="w-6 h-6"
                                    />
                                </div>
                                <span>Google 계정으로 계속하기</span>
                            </>
                        )}
                    </button>

                    <div className="flex flex-col items-center gap-6 pt-4">
                        <div className="h-[1px] w-24 bg-slate-100"></div>
                        <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-[10px] font-black text-slate-300 uppercase tracking-widest">
                            <button
                                onClick={onBranchLogin}
                                className="flex items-center gap-1.5 hover:text-indigo-500 transition-colors animate-pulse"
                            >
                                <Building2 size={12} /> 선생님 로그인
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-12 text-[10px] font-black text-slate-300 uppercase tracking-widest text-center leading-relaxed">
                로그인 시, <br />
                <button onClick={() => openPolicy('tos')} className="text-slate-400 hover:text-indigo-500 underline underline-offset-4 cursor-pointer">서비스 이용약관</button> 및 <button onClick={() => openPolicy('privacy')} className="text-slate-400 hover:text-indigo-500 underline underline-offset-4 cursor-pointer">개인정보 처리방침</button>에 동의하는 것으로 간주됩니다.
            </p>

            {/* Policy Modal */}
            {modalOpen && activePolicy && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm animate-in fade-in" onClick={() => setModalOpen(false)}></div>
                    <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[80vh] animate-in zoom-in-95">
                        <div className="flex items-center justify-between p-6 border-b border-slate-100">
                            <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                                <FileText className="text-indigo-600" size={20} />
                                {activePolicy.title}
                            </h3>
                            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <div className="prose prose-sm max-w-none text-slate-600 whitespace-pre-line leading-relaxed">
                                {activePolicy.content || '내용이 등록되지 않았습니다.'}
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="bg-slate-800 text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-900 transition-colors"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
